import { DataGridFeatureConfig } from '@waseel-asp/tpa-waseel-lib-staging';
import { ErpPaymentStatus, ErpPaymentStatusValue, PaymentStatus, PaymentStatusValue } from "../../../../../shared/constants/payment-status.enum";
import { IProvider } from "../../contract/contracts/models/contracts.model";
import { Policy } from "../../pre-auth/preauth-process/models/preauth-process.model";
import { ApprovalReasonNote, IApprovalAccidentDetail, IApprovalCareTeam, IApprovalDiagnosis, IApprovalEmergencyInfo, IApprovalEncounterInfo, IApprovalHospitalizationInfo, IApprovalItem, IApprovalSupportingInfo, IApprovalSupportingInfoDiagnosis, IApprovalSupportingInfoLabTest, IApprovalVisionPrescription, RelatedRefNo } from "../../pre-auth/preauth-request/models/preauth-request.model";
import { GRID_PAGE_DEFAULT_SIZE } from "../../../../../shared/constants/grid-constant";

interface Beneficiary {
  beneficiaryId: string
  firstName: string
  middleName: string
  lastName: string
  familyName: string
  dob: string
  gender: string
  nationalityId: string
  nationalityNameEnglish: string
  nationalityNameArabic: string
  bloodGroupId: string
  bloodGroupNameEnglish: string
  bloodGroupNameArabic: string
  preferredLanguageId: string
  languageNameEnglish: string
  languageNameArabic: string
  patientFileNo: string
  passportNumber: string
  visaNumber: string
  borderNumber: string
  visaTypeId: string
  visaTypeNameEnglish: string
  visaTypeNameArabic: string
  visaTitle: string
  visaExpiryDate: string
  residencyTypeId: string
  maritalStatusId: string
  residencyTypeNameEnglish: string
  residencyTypeNameArabic: string
  maritalStatusNameEnglish: string
  maritalStatusNameArabic: string
  createdBy: string
  createdDate: string
  lastUpdatedBy: string
  lastUpdatedDate: string
  deletedBy: string
  deletedDate: string
  deleted: boolean
  memberId: string
  fullName: string
  address: Address
  contact: Contact
}

interface Address {
  addressId: string
  countryId: string
  stateId: string
  cityId: string
  countryNameEnglish: string
  countryNameArabic: string
  stateNameEnglish: string
  stateNameArabic: string
  cityNameEnglish: string
  cityNameArabic: string
  street: string
  addressLine: string
  zipcode: string
  latitude: string
  longitude: string
  deleted: boolean
  createdBy: string
  createdDate: string
  lastUpdatedBy: string
  lastUpdatedDate: string
  deletedBy: string
  deletedDate: string
}

interface Contact {
  contactId: string
  telephone: string
  mobileNumber: string
  contactNumber: string
  emergencyNumber: string
  fax: string
  email: string
  website: string
  deleted: boolean
  createdBy: string
  createdDate: string
  lastUpdatedBy: string
  lastUpdatedDate: string
  deletedBy: string
  deletedDate: string
}

export const paymentDetailsMap = {
  [PaymentStatus.Initial]: {
    title: 'PAYMENT.STATUS.INITIAL',
    color: '#d03131',
    backgroundColor: '#f2c4c4',
    id: PaymentStatusValue[PaymentStatus.Initial]
  },
  [PaymentStatus.ReadyForPayment]: {
    title: 'PAYMENT.STATUS.READY_FOR_PAYMENT',
    color: '#a97f27',
    backgroundColor: '#f7dda2',
    id: PaymentStatusValue[PaymentStatus.ReadyForPayment]
  },
  [PaymentStatus.Done]: {
    title: 'PAYMENT.STATUS.DONE',
    color: '#36793d',
    backgroundColor: '#d7e9d8',
    id: PaymentStatusValue[PaymentStatus.Done]
  },
  [PaymentStatus.InProgress]: {
    title: 'PAYMENT.STATUS.IN_PROCESS',
    color: '#d0a029',
    backgroundColor: '#f8ebcc',
    id: PaymentStatusValue[PaymentStatus.InProgress]
  },
  [ErpPaymentStatus.success]: {
    title: 'CLAIM.VIEW_CLAIMS.SUCCESS',
    color: '#36793d',
    backgroundColor: '#d7e9d8',
    id: ErpPaymentStatusValue[ErpPaymentStatus.success]
  },
  [ErpPaymentStatus.failed]: {
    title: 'PREAUTH.STATUS_TYPE.FAILED',
    color: '#aa4549',
    backgroundColor: '#f6d6d7',
    id: ErpPaymentStatusValue[ErpPaymentStatus.failed]
  }
};

export const paymentConfig = {
  entityName: 'PAYMENT.TITLE',
  totalNet: "CONVERT_PREAUTH_TO_CLAIM.PREPARE_FOR_CLAIMS.TOTAL_NET",
  status: 'PREAUTH.PREAUTH_TRANSACTION.STATUS',
  referenceNo: 'PAYMENT.LIST.REFERENCE_NUMBER',
  finalAmount: 'PAYMENT.LIST.FINAL_APPROVED_AMOUNT',
  lossMonth: 'PAYMENT.LIST.LOSS_MONTH_SINGLE',
  identifier: 'PAYMENT.LIST.PAYMENT_IDENTIFIER',
  group: "PAYMENT.LIST.GROUP_NAME",
  date: "PAYMENT.LIST.PAYMENT_DATE",
  amount: "PAYMENT.LIST.AMOUNT",
  title: "PAYMENT.LIST.TRANSACTION_TITLE",
  provider: "PROVIDER.TITLE",
  insuranceCompany: "INSURANCE_COMPANY.TITLE",
  type: "FORM_FIELD.PAYMENT_TYPE",
  paymentMethod: "FORM_FIELD.PAYMENT_METHOD",
};

export const paymentRecentGridFeatures: DataGridFeatureConfig<PaymentList> = {
  isRowHoverEnable: true,
  showPagination: true,
  enableApiSearching: true,
  enableApiSorting: true,
  allowSearching: false,
  allowSorting: true,
  allowHideShowColumns: false,
  allowExpandRow: false,
  showCheckboxSelection: false,
  stickyFeature: {
    isStickyHeader: true
  },
  paginatorFeatures: {
    defaultPagesize: GRID_PAGE_DEFAULT_SIZE,
    totalCount: 0,
    currentPageIndex: 0,
    enableApiPagination: true
  }
};

export interface PaymentList {
  paymentId: string
  referenceNo: string
  payerId: string
  payerNameEnglish: string
  payerNameArabic: string
  providerId: string
  providerNameEnglish: string
  providerNameArabic: string
  startDate: string
  endDate: string
  paymentDate: string
  totalNet: number
  totalVat: number
  finalAmount: number
  identifier: string
  paymentType: string
  paymentTypeNameEnglish: string
  paymentTypeNameArabic: string
  paymentMethod: string
  paymentMethodNameEnglish: string
  paymentMethodNameArabic: string
  status: string
  statusNameEnglish: string
  statusNameArabic: string
  isAgreed: boolean
  agreedBy: string
  agreedDate: string
  paymentBy: string
  createdBy: string
  createdDate: string
  lastUpdatedBy: string
  lastUpdatedDate: string
  paymentClaimDetails: PaymentClaimDetail[]
  paymentAttachment: PaymentAttachmentDetail[]
  isRead: boolean
  isPaymentNoticeSent?: boolean,
  erpPaymentId: string,
  insuranceCompanyBankAccount: InsuranceCompanyBankAccount,
  providerBankAccount: ProviderBankAccount,
  paymentHistory?: PaymentHistory[];
}

export interface InsuranceCompanyBankAccount {
  bankAccountId: string;
  nameEnglish: string;
  nameArabic: string;
  accountNumber: string;
  bankAccountName: string;
  iban: string;
  countryId: string;
  stateId: string;
  cityId: string;
  countryCode: string;
  countryNameEnglish: string;
  countryNameArabic: string;
  stateNameEnglish: string;
  stateNameArabic: string;
  cityNameEnglish: string;
  cityNameArabic: string;
}

export interface ProviderBankAccount {
  bankAccountId: string;
  nameEnglish: string;
  nameArabic: string;
  accountNumber: string;
  bankAccountName: string;
  iban: string;
  countryId: string;
  stateId: string;
  cityId: string;
  countryCode: string;
  countryNameEnglish: string;
  countryNameArabic: string;
  stateNameEnglish: string;
  stateNameArabic: string;
  cityNameEnglish: string;
  cityNameArabic: string;
}

export interface PaymentClaimDetail {
  paymentClaimDetailId: string
  paymentId: string
  processsedClaimId: string
  totalNet: number
  totalVat: number
  earlyFees: number
  nphiesFees: number
  finalAmount: number
  createdBy: string
  createdDate: string
  lastUpdatedBy: string
  lastUpdatedDate: string
  claimDetail: ClaimDetail
  paymentType?: string
  paymentMethod?: string
  paymentDate?: string
  referenceNo?: string
  memberId?: string
  providerNameEnglish?: string
  providerNameArabic?: string
}

export interface PaymentHistory {
  paymentHistoryId: string;
  paymentId: string;
  referenceNo: string;
  erpPaymentId: string;
  paymentMethod: string;
  paymentMethodNameEnglish: string;
  paymentMethodNameArabic: string;
  paymentIdentifier: string;
  paymentDate: string;
  paymentAmount: number;
  currencyCode: string;
  paymentStatus: string;
  paymentStatusNameEngish: string;
  paymentStatusNameArabic: string;
  paymentStatusDescription: string;
  errorCode: string;
  errorMessage: string;
  transactionId: string;
  transactionRefNo: string;
  checkNumber: string;
  checkDate: string;
  bankOfCheck: string;
  eftType: string;
}

interface ClaimDetail {
  accountingPeriod: string
  approvalAccidentDetail: IApprovalAccidentDetail
  approvalBatchId: string
  approvalBatchIdNo: number
  approvalCareTeams: IApprovalCareTeam[]
  approvalConvertedBy: string
  approvalConvertedDate: string
  approvalDiagnosis: IApprovalDiagnosis[]
  approvalEmergencyInfo: IApprovalEmergencyInfo
  approvalEncounterInfo: IApprovalEncounterInfo
  approvalHospitalizationInfo: IApprovalHospitalizationInfo
  approvalItems: IApprovalItem[]
  approvalMainCareTeam: ApprovalMainCareTeam
  approvalPeriodEndDate: string
  approvalPeriodStartDate: string
  approvalProcessedBy: string
  approvalProcessedDate: string
  approvalReasonNotes: ApprovalReasonNote[]
  approvalRequestId: string
  approvalReuseId: string
  approvalReuseReferenceNo: string
  approvalSubmittedBy: string
  approvalSubmittedDate: string
  approvalSubType: string
  approvalSubTypeDesc: string
  approvalSupportingInfoDiagnosis: IApprovalSupportingInfoDiagnosis[]
  approvalSupportingInfoLabTests: IApprovalSupportingInfoLabTest[]
  approvalSupportingInfos: IApprovalSupportingInfo[]
  approvalType: number
  approvalTypeDesc: string
  approvalVisionPrescriptions: IApprovalVisionPrescription[]
  assignedBy: string
  assignedDate: string
  assignedTo: string
  beneficiary: Beneficiary
  claimUploadId: string
  claimUploadName: string
  convertedFromPreauthId: string
  convertedFromPreauthReferenceNo: string
  convertedToClaimReferenceNo: string
  dateOrdered: string
  eligibilityDate: string
  eligibilityId: string
  eligibilityResponseUrl: string
  eligibilityResponseValue: string
  eligibilityType: string
  eligibilityTypeNameArabic: string
  eligibilityTypeNameEnglish: string
  episodeNo: string
  hasReferralPreauth: boolean
  isAccidentRequest: boolean
  isClaimActive: boolean
  isConvertedFromPreauth: boolean
  isNewborn: boolean
  isPaymentCreated: boolean
  isRead: boolean
  isReferral: boolean
  policy: Policy
  preauthBeneficiaryFullName: string
  preauthBeneficiaryId: string
  preauthDate: string
  preauthReferenceId: string
  preauthReferenceNo: string
  preauthResponseUrl: string
  preauthResponseValue: string
  preauthType: string
  preauthTypeNameArabic: string
  preauthTypeNameEnglish: string
  prescriptionReferenceNo: string
  provider: IProvider
  providerApprovalNo: string
  readBy: string
  readDate: string
  referenceNo: string
  referralApprovalId: string
  referralBeneficiaryName: string
  referralProvider: IProvider | null
  referralReferenceNo: string
  relatedRefNos: RelatedRefNo[]
  requestBundleId: string
  responseBundleId: string
  statusId: string
  statusNameArabic: string
  statusNameEnglish: string
  subTypeId: string
  subTypeNameArabic: string
  subTypeNameEnglish: string
  totalDiscount: number
  totalNet: number
  totalPatientShare: number
  totalPayerShare: number
  totalTax: number
  typeId: string
  typeNameArabic: string
  typeNameEnglish: string
  visionPrescDateWritten: string
  visionPrescPhysicianId: string
  visionPrescPhysicianNameArabic: string
  visionPrescPhysicianNameEnglish: string
}

interface ApprovalMainCareTeam {
  approvalCareTeamId: string
  careTeamRoleId: string
  careTeamRoleNameEnglish: string
  careTeamRoleNameArabic: string
  seqNo: number
  physicianId: string
  physicianUId: string
  nameEnglish: string
  nameArabic: string
  specialityId: string
  specialityCode: string
  specialityNameEnglish: string
  specialityNameArabic: string
  roleId: string
  roleNameEnglish: string
  roleNameArabic: string
  deleted: boolean
  createdBy: string
  createdDate: string
  lastUpdatedBy: string
  lastUpdatedDate: string
  deletedBy: string
  deletedDate: string
}

export interface processedClaimsWithFees {
  processsedClaimId: string;
  earlyFees?: number
  nphiesFees?: number
  uniqueId?: string
}

export interface PaymentAttachmentDetail {
  paymentAttachmentId: string,
  paymentId: string,
  attachmentName: string,
  attachmentType: string,
  relativeFilePath: string,
  createdBy: string,
  createdDate: string
}
